import { Pagination } from '@/shared/components/Pagination';
import { ValidationErrorDisplay } from '@/shared/components/ValidationErrorDisplay';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { USER_PERMISSIONS } from '@/shared/types/permissions';
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { BrokenSubscription } from '../../types';
import { formatProblemCode, formatStatus, getStatusBadgeColor } from './utils';

type Props = {
  subscriptions: BrokenSubscription[];
  loading: boolean;
  error: string | { status: number; message?: string } | null;
  currentPage: number;
  perPage: number;
  lastPage: number;
  total: number;
  onPageChange: (p: number) => void;
  onPerPageChange: (pp: number) => void;
};

export const BrokenSubscriptionsList: React.FC<Props> = ({
  subscriptions,
  loading,
  error,
  currentPage,
  perPage,
  lastPage,
  total,
  onPageChange,
  onPerPageChange,
}) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(USER_PERMISSIONS.BrokenSubscriptionsEdit);

  const handleView = (shopifyCustomerId: number) => {
    void navigate(`/broken-subscriptions/${shopifyCustomerId}/view`);
  };

  const handleEdit = (shopifyCustomerId: number) => {
    void navigate(`/broken-subscriptions/${shopifyCustomerId}/edit`);
  };

  return (
    <CCard>
      <CCardHeader>
        <h4 className="mb-0">Broken Subscriptions</h4>
      </CCardHeader>

      <CCardBody>
        {loading && <div className="text-center">Loading...</div>}

        {error && !loading && (
          <CAlert color="danger">
            <ValidationErrorDisplay
              message={
                typeof error === 'string'
                  ? error
                  : (() => {
                      if (typeof error !== 'object' || error === null) return 'An error occurred';
                      const err = error as Record<string, unknown>;
                      if ('status' in err && typeof err.status === 'number' && err.status === 401) {
                        return 'You do not have permission to perform this action. Please contact your administrator.';
                      }
                      if ('message' in err && typeof err.message === 'string') return err.message;
                      return 'An error occurred';
                    })()
              }
            />
          </CAlert>
        )}

        {!loading && !error && subscriptions.length === 0 && (
          <CAlert color="info">No broken subscriptions found.</CAlert>
        )}

        {!loading && !error && subscriptions.length > 0 && (
          <>
            <CTable hover responsive align="middle">
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell scope="col">Shopify Customer ID</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Name</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Email</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Subscription Plan Status</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Problem</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Actions</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {subscriptions.map((subscription) => (
                  <CTableRow key={subscription.id}>
                    <CTableDataCell>{subscription.shopifyCustomerId}</CTableDataCell>
                    <CTableDataCell>{subscription.name || '-'}</CTableDataCell>
                    <CTableDataCell>{subscription.email || '-'}</CTableDataCell>
                    <CTableDataCell>
                      <CBadge color={getStatusBadgeColor(subscription.planStatus)}>
                        {formatStatus(subscription.planStatus)}
                      </CBadge>
                    </CTableDataCell>
                    <CTableDataCell>{formatProblemCode(subscription.problemCode)}</CTableDataCell>
                    <CTableDataCell>
                      <CButton
                        color="primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleView(subscription.shopifyCustomerId)}
                      >
                        View
                      </CButton>
                      {canEdit && (
                        <CButton
                          color="success"
                          size="sm"
                          onClick={() => handleEdit(subscription.shopifyCustomerId)}
                        >
                          Edit
                        </CButton>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>

            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={lastPage}
                perPage={perPage}
                total={total}
                onPageChange={onPageChange}
                onPerPageChange={onPerPageChange}
              />
            </div>
          </>
        )}
      </CCardBody>
    </CCard>
  );
};
